import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActions,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import { apiClient } from '../../lib/apiClient';
import { formatDate } from '../../lib/dateUtils';

function PrayerRequests({ user }) {
  const [church, setChurch] = useState(null);
  const [prayerRequests, setPrayerRequests] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterTab, setFilterTab] = useState(0); // 0: all, 1: pending, 2: completed
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const myChurches = await apiClient.getMyChurches(user.id);
        if (myChurches.length === 0) {
          setError('You are not assigned to any church');
          return;
        }

        const currentChurch = myChurches[0];
        setChurch(currentChurch);

        // Fetch prayer requests
        const requests = await apiClient.getPrayerRequests(currentChurch.id, user.id, user.role);
        setPrayerRequests(requests);

        // Fetch all users to get names
        const usersData = await apiClient.getAllUsers();
        setAllUsers(usersData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  const getUserName = (userId) => {
    const foundUser = allUsers.find(u => u.id === userId);
    return foundUser ? foundUser.name : userId;
  };

  const handleMarkCompleted = async (requestId) => {
    setUpdating(requestId);
    try {
      await apiClient.updatePrayerRequestStatus(requestId, user.id, 'completed');
      
      // Reload prayer requests
      const requests = await apiClient.getPrayerRequests(church.id, user.id, user.role);
      setPrayerRequests(requests);
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(null);
    }
  };

  const handleTabChange = (event, newValue) => {
    setFilterTab(newValue);
  };

  const filteredRequests = prayerRequests.filter(request => {
    if (filterTab === 1) return request.status === 'pending';
    if (filterTab === 2) return request.status === 'completed';
    return true; // All
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !church) {
    return (
      <Box>
        <Alert severity="error">{error || 'Church not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' }, mb: 3 }}>
        Prayer Requests
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filter Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={filterTab} onChange={handleTabChange}>
          <Tab label="All" />
          <Tab label="Pending" />
          <Tab label="Completed" />
        </Tabs>
      </Paper>

      {filteredRequests.length === 0 ? (
        <Alert severity="info">
          {filterTab === 1 ? 'No pending prayer requests.' : filterTab === 2 ? 'No completed prayer requests.' : 'No prayer requests yet.'}
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {filteredRequests.map((request) => (
            <Card key={request.id}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      {request.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      By: {getUserName(request.userId)}
                    </Typography>
                  </Box>
                  <Chip
                    label={request.status === 'completed' ? 'Completed' : 'Pending'}
                    color={request.status === 'completed' ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>
                <Typography variant="body1" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {request.message}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Created: {formatDate(request.createdAt)}
                  </Typography>
                  {request.status === 'completed' && request.completedByName && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      Completed by {request.completedByName}
                      {request.completedAt && ` on ${formatDate(request.completedAt)}`}
                    </Typography>
                  )}
                </Box>
              </CardContent>
              {request.status === 'pending' && (
                <CardActions>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleMarkCompleted(request.id)}
                    disabled={updating === request.id}
                    size="small"
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    {updating === request.id ? <CircularProgress size={20} /> : 'Mark as Completed'}
                  </Button>
                </CardActions>
              )}
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default PrayerRequests;

